import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type ICheckBoxProps,
    NCoreUIKitTheme,
    CheckBox,
    Field
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "CheckBox";

const USAGE_CODE = `const [isSubscribed, setIsSubscribed] = useState(false);

<CheckBox
    onCheckedChange={setIsSubscribed}
    subTitle="Hear about our campaigns."
    isChecked={isSubscribed}
    title="Marketing emails"
/>`;

const CARD_LOOK_CODE = `<CheckBox
    subTitle="Get important account changes by email."
    onCheckedChange={setIsEmailOn}
    title="Email notifications"
    isChecked={isEmailOn}
    variant="card"
/>`;

const GROUP_CODE = `const isAllSelected = channels.every(Boolean);

const isSomeSelected = channels.some(Boolean) && !isAllSelected;

<View
    aria-label="Notification channels"
    role="group"
>
    <CheckBox
        onCheckedChange={() => setChannels(channels.map(() => !isAllSelected))}
        isIndeterminate={isSomeSelected}
        isChecked={isAllSelected}
        title="Select all"
    />
    {channels.map((isSelected, index) => <CheckBox
        onCheckedChange={() => toggleChannel(index)}
        isChecked={isSelected}
        title={titles[index]}
        key={titles[index]}
    />)}
</View>`;

const TYPES = [
    "primary",
    "neutral",
    "danger",
    "warning",
    "success",
    "info"
] as const;

const CHANNEL_KEYS = [
    "sample.email",
    "checkBoxPage.channelSms",
    "checkBoxPage.channelPush"
] as const;

const InteractiveCheckBox = ({
    isIndeterminate: isInitiallyIndeterminate = false,
    isChecked: isInitiallyChecked = false,
    ...props
}: Omit<ICheckBoxProps, "onCheckedChange">) => {
    const [
        isIndeterminate,
        setIsIndeterminate
    ] = useState(isInitiallyIndeterminate);

    const [
        isChecked,
        setIsChecked
    ] = useState(Boolean(isInitiallyChecked));

    const handlePress = () => {
        setIsChecked(isIndeterminate || !isChecked);

        setIsIndeterminate(false);
    };

    return <CheckBox
        {...props}
        isIndeterminate={isIndeterminate}
        onCheckedChange={handlePress}
        isChecked={isChecked}
    />;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("checkBoxPage.selectionTitle"),
        id: "selection"
    },
    {
        title: t("checkBoxPage.typesTitle"),
        id: "types"
    },
    {
        title: t("checkBoxPage.labelTitle"),
        id: "label"
    },
    {
        title: t("checkBoxPage.cardLookTitle"),
        id: "card-look"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("checkBoxPage.groupTitle"),
        id: "group"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const CheckBoxPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        controlPaddings,
        iconSizes
    } = NCoreUIKitTheme.useContext();

    const [
        isSubscribed,
        setIsSubscribed
    ] = useState(false);

    const [
        isCardFirst,
        setIsCardFirst
    ] = useState(true);

    const [
        isCardSecond,
        setIsCardSecond
    ] = useState(false);

    const [
        channels,
        setChannels
    ] = useState([
        true,
        false,
        false
    ]);

    const isAllSelected = channels.every(Boolean);

    const isSomeSelected = channels.some(Boolean) && !isAllSelected;

    const handleToggleAll = () => {
        setChannels(channels.map(() => !isAllSelected));
    };

    const handleToggleChannel = (index: number) => {
        setChannels(channels.map((isSelected, channelIndex) => channelIndex === index ? !isSelected : isSelected));
    };

    return <Article>
        <ComponentHeader
            lead={t("checkBoxPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("checkBoxPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("checkBoxPage.overviewCaption")}
            >
                <CheckBox
                    subTitle={t("checkBoxPage.subscribeHint")}
                    title={t("checkBoxPage.subscribe")}
                    onCheckedChange={setIsSubscribed}
                    isChecked={isSubscribed}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("checkBoxPage.selectionTitle")}
            id="selection"
        >
            <Paragraph>
                {t("checkBoxPage.selectionText")}
            </Paragraph>
            <Preview>
                <InteractiveCheckBox
                    title={t("sample.stateUnchecked")}
                    isChecked={false}
                />
                <InteractiveCheckBox
                    title={t("sample.stateChecked")}
                    isChecked={true}
                />
                <InteractiveCheckBox
                    title={t("checkBoxPage.indeterminate")}
                    isIndeterminate={true}
                />
            </Preview>
        </Section>
        <Section
            title={t("checkBoxPage.typesTitle")}
            id="types"
        >
            <Paragraph>
                {t("checkBoxPage.typesText")}
            </Paragraph>
            <Preview>
                {
                    TYPES.map((type) => <InteractiveCheckBox
                        isChecked={true}
                        title={type}
                        type={type}
                        key={type}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("checkBoxPage.labelTitle")}
            id="label"
        >
            <Paragraph>
                {t("checkBoxPage.labelText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <InteractiveCheckBox
                    subTitle={t("checkBoxPage.subscribeHint")}
                    title={t("checkBoxPage.subscribe")}
                    isChecked={true}
                />
                <InteractiveCheckBox
                    hintText={t("checkBoxPage.requiredHint")}
                    title={t("checkBoxPage.terms")}
                    isRequired={true}
                />
                <InteractiveCheckBox
                    title={t("checkBoxPage.remember")}
                    isOptional={true}
                />
                <InteractiveCheckBox
                    title={t("checkBoxPage.flip")}
                    spreadBehaviour="stretch"
                    isChecked={true}
                    isFlip={true}
                />
            </Preview>
        </Section>
        <Section
            title={t("checkBoxPage.cardLookTitle")}
            id="card-look"
        >
            <Paragraph>
                {t("checkBoxPage.cardLookText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Field.Group
                    aria-label={t("checkBoxPage.cardLookTitle")}
                    role="group"
                >
                    <CheckBox
                        subTitle={t("checkBoxPage.cardLookEmailSub")}
                        title={t("checkBoxPage.cardLookEmail")}
                        onCheckedChange={setIsCardFirst}
                        isChecked={isCardFirst}
                        variant="card"
                    />
                    <CheckBox
                        subTitle={t("checkBoxPage.cardLookWeeklySub")}
                        title={t("checkBoxPage.cardLookWeekly")}
                        onCheckedChange={setIsCardSecond}
                        isChecked={isCardSecond}
                        variant="card"
                    />
                </Field.Group>
            </Preview>
            <CodeBlock
                code={CARD_LOOK_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("checkBoxPage.statesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <InteractiveCheckBox
                    title={t("checkBoxPage.disabled")}
                    isDisabled={true}
                />
                <InteractiveCheckBox
                    title={t("checkBoxPage.disabledChecked")}
                    isDisabled={true}
                    isChecked={true}
                />
                <InteractiveCheckBox
                    title={t("checkBoxPage.loading")}
                    isLoading={true}
                />
                <InteractiveCheckBox
                    hintText={t("checkBoxPage.termsError")}
                    title={t("checkBoxPage.terms")}
                    isRequired={true}
                    isError={true}
                />
            </Preview>
        </Section>
        <Section
            title={t("checkBoxPage.groupTitle")}
            id="group"
        >
            <Paragraph>
                {t("checkBoxPage.groupText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <div
                    aria-label={t("checkBoxPage.groupLabel")}
                    role="group"
                >
                    <CheckBox
                        title={t("checkBoxPage.selectAll")}
                        onCheckedChange={handleToggleAll}
                        isIndeterminate={isSomeSelected}
                        isChecked={isAllSelected}
                    />
                    <div
                        style={{
                            paddingInlineStart: iconSizes.small + controlPaddings.small
                        }}
                    >
                        {
                            CHANNEL_KEYS.map((key, index) => <CheckBox
                                onCheckedChange={() => handleToggleChannel(index)}
                                isChecked={channels[index]}
                                title={t(key)}
                                key={key}
                            />)
                        }
                    </div>
                </div>
            </Preview>
            <CodeBlock
                code={GROUP_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("checkBoxPage.a11yRole"),
                    t("checkBoxPage.a11yKeyboard"),
                    t("checkBoxPage.a11yName"),
                    t("checkBoxPage.a11yRequired"),
                    t("checkBoxPage.a11yDisabled"),
                    t("checkBoxPage.a11yDisplayOnly"),
                    t("checkBoxPage.a11yGroup")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("checkBoxPage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("checkBoxPage.propIsChecked"),
                        name: "isChecked",
                        type: "boolean"
                    },
                    {
                        description: t("checkBoxPage.propIsDefaultChecked"),
                        name: "isDefaultChecked",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("checkBoxPage.propOnCheckedChange"),
                        type: "(isChecked: boolean) => void",
                        name: "onCheckedChange"
                    },
                    {
                        description: t("checkBoxPage.propIsIndeterminate"),
                        name: "isIndeterminate",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("checkBoxPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("checkBoxPage.propSubTitle"),
                        name: "subTitle",
                        type: "string"
                    },
                    {
                        description: t("checkBoxPage.propHintText"),
                        name: "hintText",
                        type: "string"
                    },
                    {
                        description: t("checkBoxPage.propTitleStyle"),
                        type: "StyleProp<TextStyle>",
                        name: "titleStyle"
                    },
                    {
                        description: t("checkBoxPage.propSubTitleStyle"),
                        type: "StyleProp<TextStyle>",
                        name: "subTitleStyle"
                    },
                    {
                        type: "\"primary\" | \"danger\" | \"warning\" | \"neutral\" | \"success\" | \"info\"",
                        description: t("checkBoxPage.propType"),
                        defaultValue: "\"primary\"",
                        name: "type"
                    },
                    {
                        description: t("checkBoxPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("checkBoxPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("checkBoxPage.propIsError"),
                        defaultValue: "false",
                        name: "isError",
                        type: "boolean"
                    },
                    {
                        description: t("checkBoxPage.propIsRequired"),
                        defaultValue: "false",
                        name: "isRequired",
                        type: "boolean"
                    },
                    {
                        description: t("checkBoxPage.propIsOptional"),
                        defaultValue: "false",
                        name: "isOptional",
                        type: "boolean"
                    },
                    {
                        description: t("checkBoxPage.propOptionalText"),
                        name: "optionalText",
                        type: "string"
                    },
                    {
                        description: t("checkBoxPage.propIsFlip"),
                        defaultValue: "false",
                        name: "isFlip",
                        type: "boolean"
                    },
                    {
                        description: t("checkBoxPage.propSpreadBehaviour"),
                        defaultValue: "\"baseline\" · card: \"free\"",
                        type: "\"baseline\" | \"stretch\" | \"free\"",
                        name: "spreadBehaviour"
                    },
                    {
                        description: t("checkBoxPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("checkBoxPage.propVariant"),
                        type: "\"plain\" | \"card\"",
                        defaultValue: "\"plain\"",
                        name: "variant"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("checkBoxPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("checkBoxPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("checkBoxPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default CheckBoxPage;
